import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, MapPin, ChevronDown, Quote, Globe, Clock } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import useReveal from '../hooks/useReveal';
import ServiceIcon from '../components/ServiceIcon';
import CTABand from '../components/CTABand';
import { SERVICES, PILLARS, STATS, IMAGES, CONTACT, OFFICES } from '../data/content';

export default function Home() {
  const { t, lang } = useLang();
  const cn = lang === 'zh' ? 'font-cn' : '';
  useReveal([lang]);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.heroBg} alt="" className="w-full h-full object-cover animate-fadeIn" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 grain" />
        </div>
        <div className="absolute top-1/4 right-8 w-64 h-64 rounded-full border border-[#C6A15B]/25 animate-floaty hidden lg:block" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-28 pb-20 w-full">
          <div className="max-w-3xl">
            <p className={`animate-fadeUp text-[#C6A15B] tracking-[0.3em] uppercase text-xs mb-6 ${cn}`} style={{ animationDelay: '0.1s' }}>
              {t('hero.eyebrow')}
            </p>
            <h1 className={`animate-fadeUp font-serif-display text-4xl md:text-6xl font-semibold text-white leading-[1.1] mb-6 text-balance ${cn}`} style={{ animationDelay: '0.25s' }}>
              {t('hero.title')}
            </h1>
            <p className={`animate-fadeUp text-white/80 text-lg leading-relaxed max-w-2xl mb-10 ${cn}`} style={{ animationDelay: '0.4s' }}>
              {t('hero.subtitle')}
            </p>
            <div className="animate-fadeUp flex flex-wrap gap-4" style={{ animationDelay: '0.55s' }}>
              <Link to="/servicios" className={`inline-flex items-center gap-2 gold-btn rounded-full px-7 py-4 font-semibold ${cn}`}>
                {t('hero.ctaPrimary')}
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/contacto" className={`inline-flex items-center gap-2 ghost-btn rounded-full px-7 py-4 font-semibold ${cn}`}>
                {t('hero.ctaSecondary')}
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/60">
          <span className={`text-[10px] tracking-[0.3em] uppercase ${cn}`}>{t('hero.scroll')}</span>
          <ChevronDown className="w-5 h-5 animate-bounce" />
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#0B1F3A] py-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <div key={i} className="reveal text-center" data-delay={i * 90}>
              <div className="font-serif-display text-4xl md:text-5xl font-bold text-[#C6A15B]">{s.value}</div>
              <div className={`text-white/60 text-sm mt-2 ${cn}`}>{s[lang]}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal reveal-left relative">
            <div className="absolute -top-5 -left-5 w-full h-full border-2 border-[#C8142E]/45 rounded-sm hidden md:block" />
            <img src={IMAGES.aboutTeam} alt="" className="relative rounded-sm shadow-2xl w-full h-[460px] object-cover" />
          </div>
          <div className="reveal reveal-right">
            <p className={`text-[#A9853E] tracking-[0.3em] uppercase text-xs mb-3 ${cn}`}>{t('about.eyebrow')}</p>
            <h2 className={`font-serif-display text-3xl md:text-4xl font-semibold text-[#0B1F3A] mb-6 text-balance ${cn}`}>{t('about.title')}</h2>
            <div className="gold-rule mb-7" />
            <p className={`text-[#5c6470] leading-relaxed mb-4 ${cn}`}>{t('about.p1')}</p>
            <p className={`text-[#5c6470] leading-relaxed mb-8 ${cn}`}>{t('about.p2')}</p>
            <Link to="/quienes-somos" className={`inline-flex items-center gap-2 text-[#0B1F3A] font-semibold link-underline ${cn}`}>
              {t('about.readMore')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section className="navy-gradient py-20 relative overflow-hidden">
        <div className="absolute inset-0 grain" />
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center relative reveal">
          <Quote className="w-12 h-12 text-[#C6A15B] mx-auto mb-6" />
          <p className={`font-serif-display text-2xl md:text-3xl italic text-white leading-relaxed mb-6 text-balance ${cn}`}>{t('about.quote')}</p>
          <p className={`text-[#C6A15B] text-sm tracking-wide ${cn}`}>{t('about.quoteAuthor')}</p>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16 reveal">
            <p className={`text-[#A9853E] tracking-[0.3em] uppercase text-xs mb-3 ${cn}`}>{t('services.eyebrow')}</p>
            <h2 className={`font-serif-display text-3xl md:text-4xl font-semibold text-[#0B1F3A] mb-5 text-balance ${cn}`}>{t('services.title')}</h2>
            <div className="gold-rule mx-auto mb-6" />
            <p className={`text-[#5c6470] ${cn}`}>{t('services.subtitle')}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((s, i) => (
              <Link
                key={s.slug}
                to={`/servicios/${s.slug}`}
                className="svc-card lift reveal group bg-[#FAF8F3] rounded-sm border border-[#eadfca] overflow-hidden hover:shadow-2xl"
                data-delay={(i % 4) * 80}
              >
                <div className="relative h-40 overflow-hidden">
                  <img src={s.image} alt="" className="svc-img w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A]/70 to-transparent" />
                  <span className="absolute bottom-3 left-3 flex items-center justify-center w-11 h-11 rounded-sm bg-[#C6A15B] text-[#0B1F3A]">
                    <ServiceIcon name={s.icon} className="w-5 h-5" />
                  </span>
                </div>
                <div className="p-5">
                  <h3 className={`font-serif-display text-lg font-semibold text-[#0B1F3A] mb-2 group-hover:text-[#A9853E] transition-colors ${cn}`}>{s[lang].title}</h3>
                  <p className={`text-[#5c6470] text-sm leading-relaxed line-clamp-3 ${cn}`}>{s[lang].short}</p>
                  <span className={`inline-flex items-center gap-1 text-[#A9853E] text-sm font-medium mt-4 ${cn}`}>
                    {t('services.detail')} <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-16 reveal">
            <p className={`text-[#A9853E] tracking-[0.3em] uppercase text-xs mb-2 ${cn}`}>{t('pillars.eyebrow')}</p>
            <h2 className={`font-serif-display text-3xl md:text-4xl font-semibold text-[#0B1F3A] ${cn}`}>{t('pillars.title')}</h2>
            <div className="gold-rule mx-auto mt-6" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <div key={i} className="reveal lift bg-white rounded-sm p-8 border border-[#eadfca] hover:shadow-xl" data-delay={(i % 3) * 90}>
                <span className="flex items-center justify-center w-14 h-14 rounded-sm bg-[#C8142E] text-[#F1EBDF] mb-5">
                  <ServiceIcon name={p.icon} className="w-7 h-7" />
                </span>
                <h3 className={`font-serif-display text-xl font-semibold text-[#0B1F3A] mb-3 ${cn}`}>{p[lang].title}</h3>
                <p className={`text-[#5c6470] text-sm leading-relaxed ${cn}`}>{p[lang].text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTERNATIONAL PRESENCE */}
      <section className="navy-gradient py-24 relative overflow-hidden">
        <div className="absolute inset-0 grain" />
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#C8142E]/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-[#C6A15B]/10 blur-3xl" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
          <div className="text-center max-w-2xl mx-auto mb-16 reveal">
            <p className={`text-[#C6A15B] tracking-[0.3em] uppercase text-xs mb-3 ${cn}`}>{t('offices.eyebrow')}</p>
            <h2 className={`font-serif-display text-3xl md:text-4xl font-semibold text-white mb-5 text-balance ${cn}`}>{t('offices.title')}</h2>
            <div className="gold-rule mx-auto mb-6" />
            <p className={`text-white/70 ${cn}`}>{t('offices.subtitle')}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {OFFICES.map((o, i) => {
              const soon = o.status === 'soon';
              return (
                <div
                  key={o.key}
                  className={`reveal lift rounded-sm p-7 border backdrop-blur-sm ${soon ? 'bg-white/[0.03] border-white/10 border-dashed' : 'bg-white/[0.06] border-white/10'}`}
                  data-delay={(i % 4) * 90}
                >
                  <span className={`flex items-center justify-center w-14 h-14 rounded-sm mb-5 ${soon ? 'bg-white/10 text-[#C6A15B]' : 'bg-[#C8142E] text-[#F1EBDF]'}`}>
                    {soon ? <Clock className="w-7 h-7" strokeWidth={1.6} /> : <MapPin className="w-7 h-7" strokeWidth={1.6} />}
                  </span>
                  <h3 className={`font-serif-display text-2xl font-semibold text-white mb-1 ${cn}`}>{o[lang].country}</h3>
                  <p className={`text-white/60 text-sm mb-4 ${cn}`}>{o[lang].city}</p>
                  <span className={`inline-flex items-center gap-1.5 text-[11px] tracking-wide uppercase px-3 py-1 rounded-full ${soon ? 'bg-[#C6A15B]/15 text-[#D9BE84]' : 'bg-[#C8142E]/25 text-[#f2a7b0]'} ${cn}`}>
                    {soon ? <Clock className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                    {soon ? t('offices.soon') : t('offices.active')}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="reveal reveal-left">
            <p className={`text-[#A9853E] tracking-[0.3em] uppercase text-xs mb-3 ${cn}`}>{t('location.eyebrow')}</p>
            <h2 className={`font-serif-display text-3xl md:text-4xl font-semibold text-[#0B1F3A] mb-6 ${cn}`}>{t('location.title')}</h2>
            <div className="gold-rule mb-7" />
            <div className="flex items-start gap-4 mb-8">
              <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[#FAF8F3] text-[#A9853E] shrink-0">
                <MapPin className="w-5 h-5" />
              </span>
              <p className="text-[#0B1F3A] font-medium leading-relaxed">{CONTACT.addressLine1}<br />{CONTACT.addressLine2}</p>
            </div>
            <a href={CONTACT.mapsLink} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-2 gold-btn rounded-full px-6 py-3 font-semibold ${cn}`}>
              {t('location.directions')} <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
          <div className="reveal reveal-right rounded-sm overflow-hidden shadow-2xl h-[380px]">
            <iframe
              title="map"
              src="https://www.google.com/maps?q=Calle+De+Valde%C3%B3n+10,+28947+Fuenlabrada,+Madrid,+Espa%C3%B1a&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
